import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

/**
 * Post-Stripe-checkout landing page. Polls the backend up to 8 times (2s
 * intervals) until Stripe reports the session paid, then hard-navigates the
 * user back to the listing (now marked "sold"). Backend flip to `sold` +
 * seller notification happens inside the status endpoint the first time it
 * sees `payment_status == 'paid'` — this page is just the UX around it.
 */
const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function MarketplacePurchaseSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", listing_id: null, error: null });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", listing_id: null, error: "Missing session id" }); return; }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      attempt += 1;
      setAttempts(attempt);
      try {
        const r = await api.get(`/marketplace/checkout/status/${sessionId}`);
        if (cancelled) return;
        const paid = r.data?.payment_status === "paid";
        const expired = r.data?.status === "expired" || r.data?.status === "canceled";
        if (paid) {
          setState({ status: "paid", listing_id: r.data?.listing_id || null, error: null });
          return;
        }
        if (expired) {
          setState({ status: "expired", listing_id: r.data?.listing_id || null, error: null });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({ status: "timeout", listing_id: r.data?.listing_id || null, error: null });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", listing_id: null, error: formatApiError(e.response?.data?.detail) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-6 py-20" data-testid="mp-purchase-success">
      <Card className="rounded-md p-10 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto mb-3 text-[#B31E1E] animate-spin" size={30} />
            <h1 className="font-display text-2xl">Finalizing your purchase…</h1>
            <p className="mt-2 text-sm text-gray-600">Confirming with Stripe (attempt {attempts}/{MAX_ATTEMPTS})</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={36} />
            <h1 className="font-display text-2xl">Payment received!</h1>
            <p className="mt-2 text-sm text-gray-600">The seller has been notified. Watch your DMs for pickup or shipping details.</p>
            <div className="mt-6 flex gap-2 justify-center">
              {state.listing_id && (
                <Link to={`/marketplace/listings/${state.listing_id}`}>
                  <Button variant="outline" className="rounded-[35px]" data-testid="success-view-listing">View listing</Button>
                </Link>
              )}
              <Link to="/marketplace">
                <Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Back to marketplace</Button>
              </Link>
            </div>
          </>
        )}
        {state.status === "expired" && (
          <>
            <XCircle className="mx-auto mb-3 text-gray-400" size={30} />
            <h1 className="font-display text-2xl">Checkout expired</h1>
            <p className="mt-2 text-sm text-gray-600">The reservation was released. You can try again below.</p>
            {state.listing_id && (
              <Link to={`/marketplace/listings/${state.listing_id}`} className="mt-6 inline-block">
                <Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Return to listing</Button>
              </Link>
            )}
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Loader2 className="mx-auto mb-3 text-gray-400" size={30} />
            <h1 className="font-display text-2xl">Still processing</h1>
            <p className="mt-2 text-sm text-gray-600">Your bank hasn't confirmed the payment yet. Refresh in a minute or check your DMs — we'll notify you once it clears.</p>
            <Link to="/marketplace" className="mt-6 inline-block">
              <Button variant="outline" className="rounded-[35px]">Back to marketplace</Button>
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="mx-auto mb-3 text-red-500" size={30} />
            <h1 className="font-display text-2xl">Something went wrong</h1>
            <p className="mt-2 text-sm text-gray-600">{state.error || "Please try again."}</p>
            <Link to="/marketplace" className="mt-6 inline-block">
              <Button variant="outline" className="rounded-[35px]">Back to marketplace</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
